import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, renderSlot as _renderSlot, createElementVNode as _createElementVNode } from "vue"

import { ref, inject, computed } from 'vue';
import { BeakerSession } from 'beaker-kernel/src';
import BeakerCell from '../cell/BeakerCell.vue';
import { type BeakerNotebookComponentType } from './BeakerNotebook.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'BeakerNotebookPanel',
  emits: [],
  setup(__props, { expose: __expose, emit: __emit }) {

const session = inject<BeakerSession>('session');
const cellMap = inject("cell-component-mapping");
const notebook = inject<BeakerNotebookComponentType>("notebook");

const cellsContainerRef = ref(null);
const isDragActive = ref(false);
const dragSourceIndex = ref(-1);
const dragOverIndex = ref(-1);
const isDragEnabled = computed(() => session.notebook?.cells.length > 1);

const emit = __emit;


/**
 * Modifies array in place to move a cell to a new location (or any other elem)
 **/
function arrayMove(arr, old_index, new_index) {
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
}

function handleMoveCell(fromIndex, toIndex) {
    arrayMove(session.notebook.cells, fromIndex, toIndex)
}

/**
 * Sets call to item being moved
 * As well as dataTransfer so that drop target knows which one was dropped
 **/
function handleDragStart(event: DragEvent, beakerCell, index)  {
    if (event.target instanceof HTMLElement && event.dataTransfer !== null && event.target.matches(".drag-handle *")) {
        var paintTarget = event.target.closest('.beaker-cell');

        event.dataTransfer.dropEffect = 'move';
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('x-beaker/cell', JSON.stringify({
            id: beakerCell.id,
            index: index,
        }));
        if (paintTarget !== null) {
            event.dataTransfer.setDragImage(paintTarget, 0, 0);
        }
        isDragActive.value = true;
        dragSourceIndex.value = index;
    }
}

/**
 * Handles when dragging over a valid drop target
 * Both appends class to cell to mark placement of dragged cell if dropped there.,
 * as well as preventing the animation where the dragged cell animates back to
 * its place when dropped into a proper target.
 **/
function handleDragOver(event: DragEvent, beakerCell, index) {
    if (event?.dataTransfer?.types.includes("x-beaker/cell")) {
        dragOverIndex.value = index;
        event.preventDefault(); // Allow dropping
    }
}

/**
 * Handles reordering of cells if dropped within the sort-enabled cells area.
 **/
function handleDrop(event: DragEvent, index) {

    const target = event.target as HTMLElement;
    const allowedDropArea = target.closest('.drag-sort-enable');

    if (!allowedDropArea) {
        return;
    }

    const cellData = JSON.parse(event.dataTransfer?.getData('x-beaker/cell') || 'null');
    const sourceId = cellData.id;
    const sourceIndex = cellData.index;
    const targetId = session.notebook.cells[index].id

    if (sourceId !== targetId){
        arrayMove(session.notebook.cells, sourceIndex, index);
    }
}

function handleDragEnd() {
    isDragActive.value = false;
    dragOverIndex.value = -1;
    dragSourceIndex.value = -1;
}


function scrollBottomCellContainer(event) {
    if (cellsContainerRef.value) {
        cellsContainerRef.value.scrollTop = cellsContainerRef.value.scrollHeight;
    }
}


__expose({
    scrollBottomCellContainer,
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "cell-container drag-sort-enable",
    ref_key: "cellsContainerRef",
    ref: cellsContainerRef
  }, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(session).notebook.cells, (cell, index) => {
      return (_openBlock(), _createBlock(BeakerCell, {
        cell: cell,
        selected: cell.id === _unref(notebook).selectedCellId,
        index: index,
        key: `outercell-${cell.id}`,
        class: _normalizeClass(["beaker-cell", {
                selected: (cell.id === _unref(notebook).selectedCellId),
                'drag-source': (index == dragSourceIndex.value),
                'drag-above': (index === dragOverIndex.value && index < dragSourceIndex.value),
                'drag-below': (index === dragOverIndex.value && index > dragSourceIndex.value),
                'drag-itself': (index === dragOverIndex.value && index === dragSourceIndex.value ),
                'drag-active': isDragActive.value,
            }]),
        "drag-enabled": isDragEnabled.value,
        onMoveCell: handleMoveCell,
        onDragstart: ($event: any) => (handleDragStart($event, cell, index)),
        onDrop: ($event: any) => (handleDrop($event, index)),
        onDragover: ($event: any) => (handleDragOver($event, cell, index)),
        onDragend: handleDragEnd
      }, {
        default: _withCtx(() => [
          (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(cellMap)[cell.cell_type]), { cell: cell }, null, 8, ["cell"]))
        ]),
        _: 2
      }, 1032, ["cell", "selected", "index", "class", "drag-enabled", "onDragstart", "onDrop", "onDragover"]))
    }), 128)),
    _createElementVNode("div", {
      class: "drop-overflow-catcher",
      onDragover: _cache[0] || (_cache[0] = ($event: any) => {dragOverIndex.value = _unref(session).notebook.cells.length-1; $event.preventDefault();}),
      onDrop: _cache[1] || (_cache[1] = ($event: any) => (handleDrop($event, _unref(session).notebook.cells.length-1)))
    }, [
      _renderSlot(_ctx.$slots, "notebook-background")
    ], 32)
  ], 512))
}
}

})